import { fetchBot } from '@/services/services';
import { BariatricProcedureStage } from '@bariendo/lib/constants';
import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';

/**
 * Compute a patient's bariatric procedure stage when the stored extension is missing.
 *
 * The extension is written only by the Update Patient States page, so most patients
 * never had one and rendered no pill. This calls the same bot that page does, which
 * derives the stage from payment state plus appointments and persists it — so a
 * patient is computed once and read from the extension after that. Pass `skip` when
 * the extension is already there.
 */
export function usePatientProcedureStage(patientId?: string, skip?: boolean): BariatricProcedureStage | undefined {
  const medplum = useMedplum();
  const [stage, setStage] = useState<BariatricProcedureStage | undefined>(undefined);

  useEffect(() => {
    if (!patientId || skip) {
      setStage(undefined);
      return;
    }
    let active = true;
    fetchBot<{ procedureStage?: BariatricProcedureStage }>(medplum, 'update-procedure-status', {
      patient_id: patientId,
      is_healthie_patient: false,
    })
      .then((result) => {
        if (active) {
          setStage(result?.procedureStage);
        }
      })
      .catch(() => {
        if (active) {
          setStage(undefined);
        }
      });
    return () => {
      active = false;
    };
  }, [medplum, patientId, skip]);

  return stage;
}
